import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, X } from "lucide-react";
import { resolveImage, sizedImage } from "@/lib/asset-map";
import { track } from "@/lib/analytics";
import { trackConversion } from "@/hooks/use-page-view";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

/**
 * When narration finishes on an article that has a next episode in the same
 * series, show a Netflix-style 10s countdown card and auto-navigate.
 * Cancel via Esc, tap X, or scrolling up.
 */
export function AutoNextCountdown({
  currentSlug,
  nextSlug,
  nextTitle,
  nextImage,
  seconds = 10,
  blocked = false,
}: {
  currentSlug: string;
  nextSlug: string;
  nextTitle: string;
  nextImage: string | null;
  seconds?: number;
  blocked?: boolean;
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [remaining, setRemaining] = useState(seconds);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const onEnded = (e: Event) => {
      const detail = (
        e as CustomEvent<{ slug: string; lang?: string; source?: "english" | "translated" }>
      ).detail;
      if (!detail || detail.slug !== currentSlug || blocked) return;
      if (detail.source !== "english" && detail.source !== "translated") return;
      cancelledRef.current = false;
      setRemaining(seconds);
      setActive(true);
      track("autonext_started", { slug: currentSlug, next: nextSlug });
    };
    window.addEventListener("smartasaurus:narration-ended", onEnded as EventListener);
    const onNarration = (e: Event) => {
      const detail = (e as CustomEvent<{ playing?: boolean; slug?: string }>).detail;
      if (detail?.playing && detail.slug === currentSlug) {
        cancelledRef.current = true;
        setActive(false);
      }
    };
    window.addEventListener("smartasaurus:narration", onNarration as EventListener);
    return () => {
      window.removeEventListener("smartasaurus:narration-ended", onEnded as EventListener);
      window.removeEventListener("smartasaurus:narration", onNarration as EventListener);
    };
  }, [currentSlug, nextSlug, seconds, blocked]);

  useEffect(() => {
    if (!blocked) return;
    cancelledRef.current = true;
    setActive(false);
  }, [blocked]);

  useEffect(() => {
    cancelledRef.current = true;
    setActive(false);
    setRemaining(seconds);
  }, [currentSlug, seconds]);

  useEffect(() => {
    if (!active) return;
    const startedAt = performance.now();
    const initialScrollY = window.scrollY;
    const id = window.setInterval(() => {
      const elapsed = Math.floor((performance.now() - startedAt) / 1000);
      const r = Math.max(0, seconds - elapsed);
      setRemaining(r);
      if (r <= 0) {
        window.clearInterval(id);
        if (!cancelledRef.current) {
          track("autonext_fired", { slug: currentSlug, next: nextSlug });
          trackConversion("next_click", `/discovery/${nextSlug}`);
          trackConversion("recommendation_accept", `/discovery/${nextSlug}`);
          navigate({ to: "/discovery/$slug", params: { slug: nextSlug } });
        }
      }
    }, 200);

    const cancel = (reason: string) => {
      cancelledRef.current = true;
      window.clearInterval(id);
      setActive(false);
      track("autonext_cancelled", { slug: currentSlug, reason });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel("esc");
    };
    const onScroll = () => {
      if (window.scrollY < initialScrollY - 80) cancel("scroll_up");
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearInterval(id);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
    };
  }, [active, seconds, currentSlug, nextSlug, navigate]);

  if (!active) return null;
  const img = sizedImage(nextImage, 640);
  const pct = ((seconds - remaining) / seconds) * 100;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-4 md:pb-6">
      <div className="mx-auto max-w-2xl">
        <div className="relative overflow-hidden rounded-2xl border border-primary/50 bg-card/95 shadow-2xl backdrop-blur">
          <button
            type="button"
            onClick={() => {
              cancelledRef.current = true;
              setActive(false);
              track("autonext_cancelled", { slug: currentSlug, reason: "close" });
            }}
            aria-label={ui("Cancel auto-play")}
            className="absolute right-2 top-2 z-10 rounded-full bg-background/70 p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-stretch gap-3 p-3">
            {img && (
              <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg md:h-24 md:w-40">
                <img src={img} alt={nextTitle} className="h-full w-full object-cover" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                {ui("Next Episode")} · {lang === "zh" ? `${remaining} 秒后` : lang === "sv" ? `om ${remaining} s` : `in ${remaining}s`}
              </div>
              <div className="mt-1 line-clamp-2 font-display text-sm uppercase leading-tight text-foreground md:text-base">
                {nextTitle}
              </div>
              <button
                type="button"
                onClick={() => {
                  cancelledRef.current = true;
                  track("autonext_play_now", { slug: currentSlug, next: nextSlug });
                  trackConversion("next_click", `/discovery/${nextSlug}`);
                  trackConversion("recommendation_accept", `/discovery/${nextSlug}`);
                  navigate({ to: "/discovery/$slug", params: { slug: nextSlug } });
                }}
                className="plausible-event-name=AutoNext+Play mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground"
              >
                {ui("Play now")} <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
          <div className="h-1 w-full bg-muted">
            <div
              className="h-full bg-primary transition-[width] duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="mt-1.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          {ui("Press Esc or scroll up to cancel")}
        </div>
      </div>
    </div>
  );
}
